import { create } from 'zustand';

export type Role =
  | 'super_admin' | 'reseller_owner' | 'agent'
  | 'office_manager' | 'standard_user' | 'limited_user';

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
  is_super_admin: boolean;
  mfa_enabled: boolean;
  must_change_password: boolean;
  display_name: string | null;
  has_recording_pin?: boolean;
}

export interface TenantScope {
  isSuperAdmin: boolean;
  resellerId: string | null;
  customerId: string | null;
}

/**
 * The "who am I impersonating right now" descriptor. The backend returns this
 * when we start a masquerade; we pin it on the auth store so the
 * <MasqueradeBanner /> knows what to show and the API client knows we're in
 * a masquerade session (even if we only swap the access token).
 */
export interface ImpersonatingDescriptor {
  id: string;
  email: string;
  role: Role;
}

/**
 * The "who I was before I started impersonating" descriptor. Captured from
 * the current `user` field at the moment masquerade begins so the banner can
 * hint who the operator will return to. This is purely cosmetic — the actual
 * source of truth for "return to original" is the HTTP-only refresh cookie,
 * which was never rebound.
 */
export interface OriginalUserDescriptor {
  email: string;
  role: Role;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  tenant: TenantScope | null;
  impersonating: ImpersonatingDescriptor | null;
  originalUser: OriginalUserDescriptor | null;
  setSession: (p: { accessToken: string; user: AuthUser; tenant: TenantScope }) => void;
  setUser: (u: AuthUser | null) => void;
  setImpersonating: (value: ImpersonatingDescriptor | null) => void;
  setOriginalUser: (value: OriginalUserDescriptor | null) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  tenant: null,
  impersonating: null,
  originalUser: null,
  setSession: ({ accessToken, user, tenant }) => set({ accessToken, user, tenant }),
  setUser: (user) => set({ user }),
  setImpersonating: (value) => set({ impersonating: value }),
  setOriginalUser: (value) => set({ originalUser: value }),
  clear: () => set({
    accessToken: null, user: null, tenant: null,
    impersonating: null, originalUser: null,
  }),
}));
